import { useAccount, useConfig } from 'wagmi';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  LoadingState,
  EmptyState,
  Badge,
} from '@/components/ui';
import {
  CoinIcon,
  InfoIcon,
  ExternalLinkIcon,
} from '@/components/ui/Icons';
import { PositionDetailModal } from '@/components/common';
import {
  formatDate,
  getIncentiveKey,
  sortIncentivesByApr,
} from '@/utils/common';
import {
  createPool,
  createPosition,
  calculatePositionAmounts,
  isPositionInRange,
  calculateSlippageAmounts,
} from '@/utils/uniswapUtils';
import { useStakingData } from '@/hooks/useStakingData';
import { usePositionManagement } from '@/hooks/usePositionManagement';
import { useDataRefetch } from '@/contexts/DataRefetchContext';
import { useStaker } from '@/hooks/useStaker';
import { usePositionManager } from '@/hooks/usePositionManager';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { switchChain } from 'wagmi/actions';
import { chain } from '@/config/wagmi';
import { IncentiveKey } from '@/types';
import { POOL_CONFIG } from '@/config/constants';

type PositionStatus = 'staked' | 'deposited' | 'wallet';

interface UnifiedPosition {
  tokenId: string;
  status: PositionStatus;
  description: string;
  isStaked: boolean;
  liquidity: bigint;
  tickLower: number;
  tickUpper: number;
  jocxAmount?: string;
  usdtAmount?: string;
  isInRange?: boolean;
  stakedIncentiveId?: string;
}

function StatusBadge({ status }: { status: PositionStatus }) {
  const config = {
    staked: {
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: '🟢',
      label: 'Earning Rewards',
    },
    deposited: {
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: '🟡',
      label: 'Ready to Stake',
    },
    wallet: {
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: '🔵',
      label: 'In Wallet',
    },
  };

  const { color, icon, label } = config[status];

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${color}`}>
      <span>{icon}</span>
      {label}
    </span>
  );
}

function TokenPairBadge() {
  return (
    <div className="flex -space-x-2">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xs text-white font-bold border-2 border-white shadow-sm">
        J
      </div>
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-xs text-white font-bold border-2 border-white shadow-sm">
        U
      </div>
    </div>
  );
}

// Simple position card - incentive selection happens in modal
function PositionCard({
  position,
  incentive,
  onSelect,
}: {
  position: UnifiedPosition;
  incentive?: IncentiveKey;
  onSelect: () => void;
}) {
  const isTestnet = process.env.REACT_APP_IS_TESTNET;
  const uniswapBaseUrl = 'https://app.uniswap.org/pools';
  const positionUrl = `${uniswapBaseUrl}/${position.tokenId}`;

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 hover:border-slate-300 transition-all duration-200 overflow-hidden">
      {/* Header: Position ID + Status */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <TokenPairBadge />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-base font-bold text-slate-900">
                  Position #{position.tokenId}
                </span>
                {!isTestnet && (
                  <a
                    href={positionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 transition-colors"
                    title="View on Uniswap"
                  >
                    <ExternalLinkIcon className="w-4 h-4" />
                  </a>
                )}
              </div>
              <StatusBadge status={position.status} />
            </div>

            {/* Position Token Amounts */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">JOCX:</span>
                <span className="text-sm font-semibold text-slate-800">
                  {parseFloat(position.jocxAmount || '0').toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">USDT:</span>
                <span className="text-sm font-semibold text-slate-800">
                  {parseFloat(position.usdtAmount || '0').toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
              {position.isInRange !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  position.isInRange
                    ? 'bg-green-100 text-green-700'
                    : 'bg-orange-100 text-orange-700'
                }`}>
                  {position.isInRange ? 'In Range' : 'Out of Range'}
                </span>
              )}
            </div>

            {/* Staked incentive info - only show for staked positions */}
            {position.status === 'staked' && incentive && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Pool: {(Number(incentive.reward) / 1e18).toLocaleString()} JOCX</span>
                  <span>Ends: {formatDate(incentive.endTime)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="px-4 pb-4">
        <Button
          onClick={onSelect}
          className="w-full"
          variant={position.status === 'staked' ? 'secondary' : 'primary'}
        >
          {position.status === 'staked' ? 'Manage Position' :
           position.status === 'deposited' ? 'Stake or Withdraw' :
           'Stake Position'}
        </Button>
      </div>
    </div>
  );
}

function FlowGuide() {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">How staking works</h4>
      <div className="flex items-center justify-between text-xs">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold mb-1">1</div>
          <span className="text-slate-600">Stake</span>
        </div>
        <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold mb-1">2</div>
          <span className="text-slate-600">Earn</span>
        </div>
        <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center font-bold mb-1">3</div>
          <span className="text-slate-600">Unstake</span>
        </div>
        <div className="flex-1 h-0.5 bg-slate-200 mx-2" />
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold mb-1">4</div>
          <span className="text-slate-600">Claim</span>
        </div>
      </div>
    </div>
  );
}


export default function ManagePosition() {
  const { address, chainId } = useAccount();
  const wagmiConfig = useConfig();
  const { triggerRefetchStakingData } = useDataRefetch();

  const [selectedPosition, setSelectedPosition] = useState<UnifiedPosition>();

  const {
    poolData,
    rewards,
    positions,
    stakedPositions,
    createdIncentives,
    loadingUserPositions,
    loadingStakeInfos,
    loadingIncentives,
    loadingUnstakedRewards,
    fetchUserPositions,
    fetchStakeInfos,
    fetchIncentives,
    fetchUnstakedRewards,
  } = useStakingData();

  const {
    setSelectedUserTokenId,
    setStakedTokenId,
    loadingStakedRewards,
    rewardInfo,
    fetchStakedRewards,
  } = usePositionManagement({
    positions,
    stakedPositions,
    selectedIncentive: selectedPosition?.stakedIncentiveId
      ? createdIncentives.find(i => i.id === selectedPosition.stakedIncentiveId)
      : undefined,
    poolData,
  });

  // Sort incentives by APR
  const sortedIncentives = useMemo(() => {
    return sortIncentivesByApr(createdIncentives);
  }, [createdIncentives]);

  // Filter to only live incentives
  const liveIncentives = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return sortedIncentives.filter((i) => now <= Number(i.endTime));
  }, [sortedIncentives]);

  // Create pool instance
  const pool = useMemo(() => {
    if (!poolData.sqrtPriceX96 || !poolData.liquidity || poolData.tick === undefined) {
      return null;
    }
    try {
      return createPool(poolData.sqrtPriceX96, poolData.liquidity, poolData.tick);
    } catch {
      return null;
    }
  }, [poolData.sqrtPriceX96, poolData.liquidity, poolData.tick]);

  const calculatePositionDetails = (
    liquidity: bigint,
    tickLower: number,
    tickUpper: number
  ): { jocxAmount: string; usdtAmount: string; isInRange: boolean } | null => {
    if (!pool || liquidity === BigInt(0)) return null;
    try {
      const position = createPosition(pool, tickLower, tickUpper, liquidity);
      if (!position) return null;
      const amounts = calculatePositionAmounts(position);
      return {
        jocxAmount: amounts.jocxAmount,
        usdtAmount: amounts.usdtAmount,
        isInRange: isPositionInRange(position),
      };
    } catch {
      return null;
    }
  };

  // Build unified positions with incentive IDs
  const unifiedPositions = useMemo((): UnifiedPosition[] => {
    const result: UnifiedPosition[] = [];

    stakedPositions.forEach((pos) => {
      const details = calculatePositionDetails(pos.liquidity, pos.tickLower, pos.tickUpper);
      result.push({
        tokenId: pos.tokenId,
        status: pos.staked ? 'staked' : 'deposited',
        description: 'JOCX/USDT',
        isStaked: pos.staked,
        liquidity: pos.liquidity,
        tickLower: pos.tickLower,
        tickUpper: pos.tickUpper,
        jocxAmount: details?.jocxAmount,
        usdtAmount: details?.usdtAmount,
        isInRange: details?.isInRange,
        stakedIncentiveId: pos.stakedIncentiveId,
      });
    });

    positions.forEach((pos) => {
      const alreadyAdded = result.some((p) => p.tokenId === pos.tokenId);
      if (!alreadyAdded) {
        const details = calculatePositionDetails(pos.liquidity, pos.tickLower, pos.tickUpper);
        result.push({
          tokenId: pos.tokenId,
          status: 'wallet',
          description: 'JOCX/USDT',
          isStaked: false,
          liquidity: pos.liquidity,
          tickLower: pos.tickLower,
          tickUpper: pos.tickUpper,
          jocxAmount: details?.jocxAmount,
          usdtAmount: details?.usdtAmount,
          isInRange: details?.isInRange,
        });
      }
    });

    return result;
  }, [stakedPositions, positions, pool]);

  // Get incentive for a staked position (from stakedIncentiveId)
  const getStakedIncentive = (position: UnifiedPosition): IncentiveKey | undefined => {
    if (position.stakedIncentiveId) {
      return sortedIncentives.find(i => i.id === position.stakedIncentiveId);
    }
    return undefined;
  };


  useEffect(() => {
    if (!poolData.poolAddress) return;
    fetchIncentives();
    fetchStakeInfos();
  }, [poolData.poolAddress]);

  const handlePositionSelect = async (position: UnifiedPosition) => {
    setSelectedPosition(position);
    if (position.status === 'staked') {
      setStakedTokenId(position.tokenId);
      await fetchStakedRewards();
    } else if (position.status === 'deposited') {
      setStakedTokenId(position.tokenId);
      await fetchUnstakedRewards();
    } else {
      setSelectedUserTokenId(position.tokenId);
    }
  };

  const [isStaking, setIsStaking] = useState(false);
  const [isTransferStaking, setIsTransferStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isRemovingLiquidity, setIsRemovingLiquidity] = useState(false);

  const { stakeToken, unstakeToken, claimReward, withdrawToken } = useStaker();
  const { safeTransferToStake, removeLiquidity } = usePositionManager();

  const checkChainId = async () => {
    if (chainId !== chain.id) {
      await switchChain(wagmiConfig, { chainId: chain.id });
    }
  };

  const handleTransactionSuccess = async (
    fetchStakeInfo?: boolean,
    fetchRewards?: boolean,
    fetchUserPosition?: boolean
  ) => {
    if (fetchRewards) {
      await fetchStakedRewards();
      await fetchUnstakedRewards();
    }
    if (fetchUserPosition) {
      await fetchUserPositions();
    }
    if (fetchStakeInfo) {
      await fetchStakeInfos();
    }
    triggerRefetchStakingData();
  };

  const handleTransferStake = async (incentive?: IncentiveKey) => {
    if (!selectedPosition || !incentive) return;
    await checkChainId();

    setIsTransferStaking(true);
    try {
      await safeTransferToStake({
        tokenId: BigInt(selectedPosition.tokenId),
        data: getIncentiveKey(incentive),
      });
      await handleTransactionSuccess(true, true, true);
      toast.success('Position staked successfully!');
      setSelectedPosition(undefined);
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to stake. Please try again.');
    } finally {
      setIsTransferStaking(false);
    }
  };

  const handleStake = async (incentive?: IncentiveKey) => {
    if (!selectedPosition || !incentive) return;
    await checkChainId();

    setIsStaking(true);
    try {
      await stakeToken({
        key: incentive,
        tokenId: BigInt(selectedPosition.tokenId),
      });
      await handleTransactionSuccess(true, true, false);
      toast.success('Position staked successfully!');
      setSelectedPosition(undefined);
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to stake. Please try again.');
    } finally {
      setIsStaking(false);
    }
  };

  const handleUnstake = async () => {
    if (!selectedPosition) return;
    const incentive = getStakedIncentive(selectedPosition);
    if (!incentive) return;
    await checkChainId();

    setIsUnstaking(true);
    try {
      await unstakeToken({
        key: incentive,
        tokenId: BigInt(selectedPosition.tokenId),
      });
      await handleTransactionSuccess(true, true, false);
      toast.success('Position unstaked successfully!');
      setSelectedPosition(undefined);
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to unstake. Please try again.');
    } finally {
      setIsUnstaking(false);
    }
  };

  const handleClaimRewards = async () => {
    await checkChainId();

    setIsClaiming(true);
    try {
      await claimReward();
      await handleTransactionSuccess(false, true, false);
      toast.success('Rewards claimed successfully!');
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to claim. Please try again.');
    } finally {
      setIsClaiming(false);
    }
  };

  const handleWithdraw = async () => {
    if (!selectedPosition) return;
    await checkChainId();

    setIsWithdrawing(true);
    try {
      await withdrawToken({
        tokenId: BigInt(selectedPosition.tokenId),
      });
      await handleTransactionSuccess(true, true, true);
      toast.success('Position withdrawn successfully!');
      setSelectedPosition(undefined);
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to withdraw. Please try again.');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!selectedPosition || selectedPosition.status !== 'wallet') return;
    await checkChainId();

    setIsRemovingLiquidity(true);
    try {
      const amount0Min = calculateSlippageAmounts(
        selectedPosition.jocxAmount ? BigInt(Math.floor(parseFloat(selectedPosition.jocxAmount) * 1e18)) : BigInt(0)
      ).min;
      const amount1Min = calculateSlippageAmounts(
        selectedPosition.usdtAmount ? BigInt(Math.floor(parseFloat(selectedPosition.usdtAmount) * 1e6)) : BigInt(0)
      ).min;

      await removeLiquidity({
        tokenId: BigInt(selectedPosition.tokenId),
        liquidity: selectedPosition.liquidity,
        amount0Min,
        amount1Min,
      });

      await handleTransactionSuccess(false, false, true);
      toast.success('Liquidity removed successfully!');
      setSelectedPosition(undefined);
    } catch (error: any) {
      toast.error(error.shortMessage || 'Failed to remove liquidity. Please try again.');
    } finally {
      setIsRemovingLiquidity(false);
    }
  };

  const currentRewards = useMemo(() => {
    if (!selectedPosition) return '0';
    if (selectedPosition.status === 'staked' && rewardInfo) {
      return (Number(rewardInfo[0]) / 1e18).toString();
    }
    return rewards;
  }, [selectedPosition, rewardInfo, rewards]);

  const isLoading = loadingIncentives || loadingUserPositions || loadingStakeInfos;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Positions List - Single Column */}
      <Card glow>
        <CardHeader
          icon={<CoinIcon className="w-5 h-5 text-white" />}
          title="Your Positions"
          subtitle={`${unifiedPositions.length} position${unifiedPositions.length !== 1 ? 's' : ''} found`}
        />
        <CardContent>
          {isLoading ? (
            <LoadingState
              title="Loading Positions..."
              subtitle="Fetching your liquidity positions"
            />
          ) : unifiedPositions.length === 0 ? (
            <EmptyState
              icon={<InfoIcon className="w-8 h-8 text-gray-400" />}
              title="No Positions Found"
              subtitle="Add liquidity first to start earning staking rewards"
              action={
                <Badge variant="info" className="inline-flex items-center">
                  <InfoIcon className="w-4 h-4 mr-2" />
                  Switch to "Add Liquidity" tab
                </Badge>
              }
            />
          ) : (
            <div className="space-y-4">
              {unifiedPositions.map((position) => {
                const stakedIncentive = getStakedIncentive(position);
                return (
                  <PositionCard
                    key={position.tokenId}
                    position={position}
                    incentive={stakedIncentive}
                    onSelect={() => handlePositionSelect(position)}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Position Detail Modal */}
      <PositionDetailModal
        isOpen={!!selectedPosition}
        onClose={() => setSelectedPosition(undefined)}
        position={selectedPosition ? {
          tokenId: selectedPosition.tokenId,
          token0: '',
          token1: '',
          fee: POOL_CONFIG.JOCX_USDT_FEE,
          tickLower: selectedPosition.tickLower,
          tickUpper: selectedPosition.tickUpper,
          liquidity: selectedPosition.liquidity,
          tokensOwed0: '0',
          tokensOwed1: '0',
          staked: selectedPosition.status === 'staked',
        } : null}
        isStaked={selectedPosition?.status === 'staked'}
        incentive={selectedPosition ? getStakedIncentive(selectedPosition) || liveIncentives[0] : undefined}
        liveIncentives={liveIncentives}
        unclaimedRewards={currentRewards}
        jocxAmount={selectedPosition?.jocxAmount}
        usdtAmount={selectedPosition?.usdtAmount}
        isInRange={selectedPosition?.isInRange}
        onStake={async (selectedIncentive) => {
          if (selectedPosition?.status === 'wallet') {
            await handleTransferStake(selectedIncentive);
          } else {
            await handleStake(selectedIncentive);
          }
        }}
        onUnstake={handleUnstake}
        onClaim={handleClaimRewards}
        onWithdraw={handleWithdraw}
        onRemoveLiquidity={selectedPosition?.status === 'wallet' ? handleRemoveLiquidity : undefined}
      />

      {/* Flow Guide */}
      <div className="mt-6">
        <FlowGuide />
      </div>
    </div>
  );
}
